import React from 'react'
import { Formik } from 'formik'
import { Form, Button, Link, Flex, Input } from '@sketch/components'
import Panel from '../Panel'
import * as yup from 'yup'
import {
  FormWrapper,
  Description,
  DescriptionText,
  EditButton,
  FormActions,
} from './WorkspaceSettingsEncryptionPanel.styles'
import { useConfigureWorkspaceEncryptionKeysMutation } from '@sketch/gql-types'
import { useToast } from '@sketch/toasts'

interface WorkspaceSettingsEncryptionPanelProps {
  workspaceId: string
  encryptionKeys?: string[]
  roleArn?: string
}

type Values = {
  encryptionKey: string
  roleArn: string
}

// AWS KMS Key ARN format: arn:aws:kms:<region>:<account-id>:key/<key-id>
// Supports partitions: aws, aws-cn, aws-us-gov
const AWS_KMS_ARN_PATTERN =
  /^arn:aws(-[a-z]+)?:kms:[a-z0-9-]+:[0-9]{12}:key\/.+$/

// AWS IAM Role ARN format: arn:aws:iam::<account-id>:role/<role-name>
// Supports partitions: aws, aws-cn, aws-us-gov
const AWS_ROLE_ARN_PATTERN = /^arn:aws(-[a-z]+)?:iam::[0-9]{12}:role\/.+$/

const MAX_KMS_ARNS = 10

const validateKmsArns = (value: string | undefined): boolean | string => {
  if (!value || value.trim() === '') {
    return 'Enter at least one AWS KMS ARN'
  }

  const arns = value
    .split(',')
    .map(s => s.trim())
    .filter(s => s !== '')

  if (arns.length === 0) {
    return 'Enter at least one AWS KMS ARN'
  }

  if (arns.length > MAX_KMS_ARNS) {
    return `Maximum ${MAX_KMS_ARNS} encryption keys allowed`
  }

  for (const arn of arns) {
    if (!AWS_KMS_ARN_PATTERN.test(arn)) {
      return 'Enter valid AWS KMS ARNs'
    }
  }

  return true
}

const schema = yup.object().shape({
  encryptionKey: yup
    .string()
    .trim()
    .test('valid-kms-arns', function (value) {
      const result = validateKmsArns(value)
      if (result === true) {
        return true
      }
      return this.createError({ message: result as string })
    }),
  roleArn: yup
    .string()
    .trim()
    .required('Enter an AWS Role ARN')
    .matches(AWS_ROLE_ARN_PATTERN, 'Enter a valid AWS Role ARN'),
})

export const WorkspaceSettingsEncryptionPanel: React.FC<
  WorkspaceSettingsEncryptionPanelProps
> = ({ workspaceId, encryptionKeys, roleArn }) => {
  const { showToast } = useToast()
  const [isEditingForm, setIsEditingForm] = React.useState(false)

  const [updateEncryptionSettings] =
    useConfigureWorkspaceEncryptionKeysMutation({
      onError: 'show-toast',
      onCompleted: () => {
        showToast('Configuration successfully saved', 'positive')
        setIsEditingForm(false)
      },
    })

  const onSubmit = async (value: Values) => {
    await updateEncryptionSettings({
      variables: {
        encryptionParams: {
          workspaceIdentifier: workspaceId,
          roleArn: value.roleArn === '' ? null : value.roleArn,
          kmsArns: value.encryptionKey.split(',').map(s => s.trim()),
        },
      },
    })
  }

  const initialValues: Values = {
    encryptionKey: encryptionKeys?.join(', ') ?? '',
    roleArn: roleArn ?? '',
  }

  const editRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    const element = editRef.current
    if (element !== null && isEditingForm) {
      element.focus()
    }
  }, [isEditingForm])

  return (
    <Panel>
      <Panel.Title>Set Up Encryption</Panel.Title>
      <Description>
        <DescriptionText>
          Configure the encryption keys and specify the AWS role to enable
          encryption.{' '}
          <Link
            href="https://www.sketch.com/redirect/app/docs/encryption"
            external
          >
            Learn More
          </Link>
        </DescriptionText>
        <EditButton
          onClick={() => {
            setIsEditingForm(true)
          }}
        >
          Edit...
        </EditButton>
      </Description>

      <Panel.Body>
        <FormWrapper>
          <Formik
            onSubmit={onSubmit}
            initialValues={initialValues}
            validationSchema={schema}
            validateOnMount
          >
            {formikBag => (
              <Form>
                <Flex flexDirection="column">
                  <Form.Field
                    name="encryptionKey"
                    label="Encryption key"
                    errorText={
                      formikBag.touched.encryptionKey
                        ? formikBag.errors.encryptionKey
                        : undefined
                    }
                  >
                    <Input
                      ref={editRef}
                      name="encryptionKey"
                      type="text"
                      aria-label="Encryption key"
                      placeholder="arn:aws:kms:::key/..."
                      value={formikBag.values.encryptionKey}
                      onChange={formikBag.handleChange}
                      onBlur={formikBag.handleBlur}
                      disabled={!isEditingForm}
                    />
                  </Form.Field>

                  <Form.Field
                    name="roleArn"
                    label="AWS Role ARN"
                    errorText={
                      formikBag.touched.roleArn
                        ? formikBag.errors.roleArn
                        : undefined
                    }
                  >
                    <Input
                      name="roleArn"
                      aria-label="AWS Role ARN"
                      type="text"
                      value={formikBag.values.roleArn}
                      placeholder="arn:aws:iam:::role/..."
                      onChange={formikBag.handleChange}
                      onBlur={formikBag.handleBlur}
                      disabled={!isEditingForm}
                    />
                  </Form.Field>

                  {isEditingForm && (
                    <FormActions>
                      <Button
                        type="button"
                        disabled={formikBag.isSubmitting}
                        size="40"
                        onClick={e => {
                          formikBag.handleReset(e)
                          setIsEditingForm(false)
                        }}
                      >
                        Cancel
                      </Button>

                      <Button
                        type="submit"
                        variant="primary"
                        size="40"
                        disabled={formikBag.isSubmitting || !formikBag.isValid}
                        loading={formikBag.isSubmitting}
                      >
                        Save Changes
                      </Button>
                    </FormActions>
                  )}
                </Flex>
              </Form>
            )}
          </Formik>
        </FormWrapper>
      </Panel.Body>
    </Panel>
  )
}
